import { useState } from 'react';
import { ArrowsClockwise, Broadcast, Globe, Shield, ShieldCheck } from '@phosphor-icons/react';
import { ReportExportControl } from '../components/report-export-control';
import { ReportKpiTile } from '../components/report-kpi-tile';
import { ReportRangeControl } from '../components/report-range-control';
import { DeliveryDataNotice, ReportRequestState } from '../components/report-request-state';
import { useDeliverabilityReport } from '../hooks/use-analytics';
import { useReportRange } from '../hooks/use-report-range';
import type { DeliverabilityReport } from '../types/analytics-api.types';
import { fmtNum, fmtPct, fmtShortDate } from '../utils/report-format';
import { RATES } from '../utils/report-rates';
import { cn } from '@/lib/utils';

function toneFor(
  key: 'deliveryRate' | 'bounceRate' | 'complaintRate',
  value: number | null | undefined,
  thresholds: { bounceRate: number; complaintRate: number }
): 'success' | 'warning' | 'error' | 'neutral' {
  if (value == null) return 'neutral';
  if (key === 'deliveryRate') {
    if (value >= 0.97) return 'success';
    if (value >= 0.9) return 'warning';
    return 'error';
  }
  const limit = thresholds[key];
  if (value >= limit) return 'error';
  if (value >= limit * 0.6) return 'warning';
  return 'success';
}

function toneLabel(tone: string) {
  if (tone === 'error') return 'Above threshold';
  if (tone === 'warning') return 'Watch';
  if (tone === 'success') return 'Healthy';
  return 'No data';
}

function ThresholdRow({
  label,
  value,
  threshold,
  tone,
}: {
  label: string;
  value: number | null;
  threshold: number;
  tone: string;
}) {
  const val = value ?? 0;
  const scaleMax = Math.max(threshold * 2, val * 1.15, 0.0001);
  const fillPct = Math.min(100, Math.round((val / scaleMax) * 100));
  const markPct = Math.min(100, Math.round((threshold / scaleMax) * 100));
  const fillColor = tone === 'error' ? '#C1443C' : tone === 'warning' ? '#D89A2E' : '#1baf7a';

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <span className="font-medium text-neutral-900">{label}</span>
        <span className="text-xs text-neutral-500">
          {fmtPct(value)} vs. {fmtPct(threshold)} threshold
        </span>
      </div>
      <div className="relative h-2.5 overflow-hidden rounded-full bg-[#F4F5F6]">
        <div
          className="h-full min-w-[2px] rounded-full"
          style={{ width: `${fillPct}%`, background: fillColor }}
        />
        <div
          className="absolute bottom-[-3px] top-[-3px] w-0.5 bg-[#8A8F94]"
          style={{ left: `${markPct}%` }}
        />
      </div>
    </div>
  );
}

const MVP2_ITEMS = [
  {
    icon: Broadcast,
    label: 'Sender reputation monitoring',
    body: 'Ongoing IP/domain reputation scoring with mailbox-provider feedback.',
  },
  {
    icon: ArrowsClockwise,
    label: 'Feedback loops (FBL)',
    body: 'Automatic complaint signals from mailbox providers routed back into suppression.',
  },
  {
    icon: Globe,
    label: 'Dedicated sending IPs',
    body: "Isolate a customer's sending reputation onto its own IP pool.",
  },
  {
    icon: ShieldCheck,
    label: 'DKIM / SPF / DMARC setup',
    body: 'Guided domain-authentication configuration and monitoring. Sender infrastructure is managed manually until then.',
  },
];

type DeliverabilityBodyProps = {
  days: number;
  from?: string;
  to?: string;
  campaignId?: string | null;
  groupBy: 'domain' | 'day';
  onGroupByChange: (next: 'domain' | 'day') => void;
  scopeLabel?: string;
};

export function DeliverabilityBody({
  from,
  to,
  campaignId,
  groupBy,
  onGroupByChange,
  scopeLabel,
}: DeliverabilityBodyProps) {
  const query = useDeliverabilityReport({ from, to, groupBy, campaignId });
  const data = query.data;

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div
          className="inline-flex items-center overflow-hidden rounded-md border border-neutral-300 bg-white"
          role="group"
          aria-label="Group deliverability"
        >
          {(
            [
              { key: 'domain' as const, label: 'By domain' },
              { key: 'day' as const, label: 'By day' },
            ] as const
          ).map((option, index) => (
            <button
              key={option.key}
              type="button"
              aria-pressed={groupBy === option.key}
              onClick={() => onGroupByChange(option.key)}
              className={cn(
                'px-3 py-1.5 text-[13px] font-medium',
                index > 0 && 'border-l border-neutral-300',
                groupBy === option.key
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-neutral-700 hover:bg-neutral-50'
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      <ReportRequestState
        isLoading={query.isLoading}
        error={query.error}
        invalidRange={!!from && !!to && from > to}
        skeleton="deliverability"
      />
      {data ? <DeliverabilityResults data={data} scopeLabel={scopeLabel} /> : null}
    </>
  );
}

function DeliverabilityResults({
  data,
  scopeLabel,
}: {
  data: DeliverabilityReport;
  scopeLabel?: string;
}) {
  const windowLabel = `${fmtShortDate(data.period.from)} – ${fmtShortDate(data.period.to)}`;
  const scope = data.scope.campaignId == null ? 'All sends' : scopeLabel || data.scope.campaignId;

  if (data.totals.sent === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-lg border border-neutral-200 bg-white p-8 text-center shadow-sm">
        <Shield className="size-7 text-neutral-400" aria-hidden />
        <p className="font-medium text-neutral-900">No sends in this range</p>
        <p className="max-w-sm text-xs text-neutral-500">
          Nothing was sent in {windowLabel} for {scope}, so there is no deliverability data to show.
          Try a wider range.
        </p>
      </div>
    );
  }

  const deliveryValue = data.deliveryDataAvailable ? data.totals.deliveryRate : null;
  const deliveryTone = toneFor('deliveryRate', deliveryValue, data.thresholds);
  const bounceTone = toneFor('bounceRate', data.totals.bounceRate, data.thresholds);
  const complaintTone = toneFor('complaintRate', data.totals.complaintRate, data.thresholds);
  const bounceTotal = data.totals.bounces;
  const hardPct = bounceTotal ? Math.round((data.totals.hardBounces / bounceTotal) * 100) : 0;
  const softPct = bounceTotal ? 100 - hardPct : 0;
  const warnedKeys = new Set(data.warnings.map((warning) => warning.key));

  return (
    <>
      {data.deliveryDataAvailable ? null : <DeliveryDataNotice />}
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
        <p className="text-xs text-neutral-500">
          Window: {windowLabel} · {scope}
          {data.scope.campaignId ? '' : ' · organization'}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {(
          [
            {
              key: 'deliveryRate' as const,
              label: 'Delivery rate',
              value: deliveryValue,
              tone: deliveryTone,
              hint: data.deliveryDataAvailable
                ? RATES.deliveryRate.hint
                : 'Delivery data not received yet',
              digits: 1,
            },
            {
              key: 'bounceRate' as const,
              label: 'Bounce rate',
              value: data.totals.bounceRate,
              tone: bounceTone,
              hint: `${RATES.bounceRate.hint} · threshold ${fmtPct(data.thresholds.bounceRate)}`,
              digits: 1,
            },
            {
              key: 'complaintRate' as const,
              label: 'Complaint rate',
              value: data.totals.complaintRate,
              tone: complaintTone,
              hint: `${RATES.complaintRate.hint} · threshold ${fmtPct(data.thresholds.complaintRate, 2)}`,
              digits: 2,
            },
          ] as const
        ).map((tile) => (
          <ReportKpiTile
            key={tile.key}
            label={tile.label}
            value={fmtPct(tile.value, tile.digits)}
            hint={tile.hint}
            flagged={tile.tone === 'warning'}
            critical={tile.tone === 'error'}
            labelAside={
              <span
                className={cn(
                  'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                  tile.tone === 'error' && 'bg-[#FDECEC] text-[#B3261E]',
                  tile.tone === 'warning' && 'bg-[#FFF4E5] text-[#8A5B00]',
                  tile.tone === 'success' && 'bg-[#E8F7EE] text-[#0A7A3E]',
                  tile.tone === 'neutral' && 'bg-neutral-100 text-neutral-600'
                )}
              >
                {toneLabel(tile.tone)}
              </span>
            }
          />
        ))}
      </div>

      <div className="flex flex-col gap-4 rounded-lg border border-neutral-200 bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-neutral-900">Bounce and complaint thresholds</h2>
        <ThresholdRow
          label="Bounce rate"
          value={data.totals.bounceRate}
          threshold={data.thresholds.bounceRate}
          tone={bounceTone}
        />
        <ThresholdRow
          label="Complaint rate"
          value={data.totals.complaintRate}
          threshold={data.thresholds.complaintRate}
          tone={complaintTone}
        />
        <div className="flex flex-wrap items-center gap-6 border-t border-neutral-100 pt-4">
          <div>
            <p className="text-xs font-medium text-neutral-500">Hard bounces</p>
            <p className="text-lg font-semibold text-neutral-900">
              {fmtNum(data.totals.hardBounces)}
              <span className="text-xs font-normal text-neutral-500"> ({hardPct}%)</span>
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-neutral-500">Soft bounces</p>
            <p className="text-lg font-semibold text-neutral-900">
              {fmtNum(data.totals.softBounces)}
              <span className="text-xs font-normal text-neutral-500"> ({softPct}%)</span>
            </p>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-sm">
        <div className="border-b border-neutral-200 p-4">
          <h2 className="text-base font-semibold text-neutral-900">
            {data.period.groupBy === 'day' ? 'By day' : 'By domain'}
          </h2>
          <p className="mt-0.5 text-xs text-neutral-500">
            Rows the API returned, with a total underneath. A domain where everything bounced shows
            a 0% delivery rate. Sender authentication (DKIM, SPF, DMARC) and sending IPs are not in
            this report — they are managed manually.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[880px]">
            <thead>
              <tr>
                {(
                  [
                    'Domain',
                    'Sent',
                    'Delivered',
                    'Delivery',
                    'Bounces',
                    'Bounce rate',
                    'Complaints',
                    'Complaint rate',
                  ] as const
                ).map((heading) => (
                  <th
                    key={heading}
                    className={cn(
                      'px-3 py-2.5 text-xs font-semibold text-neutral-500',
                      heading === 'Domain' ? 'text-left' : 'text-right'
                    )}
                  >
                    {data.period.groupBy === 'day' && heading === 'Domain' ? 'Day' : heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.items.map((row) => (
                <tr key={row.key ?? 'row'} className="border-t border-neutral-100">
                  <td className="px-3 py-2.5 font-medium text-neutral-900">
                    {row.key || '—'}
                    {warnedKeys.has(row.key) ? (
                      <span className="ml-2 inline-flex rounded-full bg-[#FFF4E5] px-2 py-0.5 text-xs font-medium text-[#8A5B00]">
                        Over threshold
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmtNum(row.sent)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    {data.deliveryDataAvailable ? fmtNum(row.delivered) : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    {data.deliveryDataAvailable ? fmtPct(row.deliveryRate) : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    {fmtNum(row.bounces)}
                    <span className="block text-xs text-neutral-500">
                      {fmtNum(row.hardBounces)} hard · {fmtNum(row.softBounces)} soft
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmtPct(row.bounceRate)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmtNum(row.complaints)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    {fmtPct(row.complaintRate, 2)}
                  </td>
                </tr>
              ))}
              <tr className="border-t border-neutral-200 bg-neutral-50 font-medium">
                <td className="px-3 py-2.5 text-neutral-900">Total</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{fmtNum(data.totals.sent)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {data.deliveryDataAvailable ? fmtNum(data.totals.delivered) : '—'}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {data.deliveryDataAvailable ? fmtPct(data.totals.deliveryRate) : '—'}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {fmtNum(data.totals.bounces)}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {fmtPct(data.totals.bounceRate)}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {fmtNum(data.totals.complaints)}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {fmtPct(data.totals.complaintRate, 2)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex flex-col gap-3 rounded-lg border border-neutral-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-neutral-900">Coming in a later milestone</h2>
          <span className="inline-flex rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-600">
            MVP-2
          </span>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {MVP2_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.label}
                className="relative flex items-start gap-3 rounded-xl border border-dashed border-[#DEE0E3] bg-[#F7F7F8] p-3.5 opacity-70"
              >
                <Icon className="mt-0.5 size-5 shrink-0 text-neutral-400" aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-neutral-600">{item.label}</p>
                  <p className="mt-0.5 text-xs text-neutral-500">{item.body}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

export function ReportsDeliverabilityPage() {
  const { range } = useReportRange();
  const [groupBy, setGroupBy] = useState<'domain' | 'day'>('domain');

  return (
    <div className="flex h-full flex-col gap-5 overflow-y-auto p-4 sm:p-6">
      <div>
        <h1 className="text-2xl font-semibold text-neutral-900">Deliverability</h1>
        <p className="mt-0.5 text-neutral-600">
          Delivery, bounce and complaint rates across all sends, against the thresholds this
          workspace returns.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <ReportRangeControl />
        <ReportExportControl
          report="deliverability"
          formats={['csv']}
          getFilters={() => ({ from: range.from, to: range.to, groupBy })}
        />
      </div>

      <DeliverabilityBody
        days={range.days}
        from={range.from}
        to={range.to}
        groupBy={groupBy}
        onGroupByChange={setGroupBy}
      />
    </div>
  );
}
